<template>
  <div>
    <v-overlay v-if="loading"
      absolute
      style="z-index: 1000">
      <v-progress-circular indeterminate
        size="64"
        color="primary"></v-progress-circular>
    </v-overlay>
    <confirm ref="confirmStorno"
      @selection="cancelReceipt" />
    <confirm ref="confirmFiscalize"
      @selection="fiscalizeReceipt" />
    <v-container>
      <v-row class="justify-center">
        <v-col cols="12"
          sm="11"
          lg="11"
          :class="['px-0']">
          <div class="table-header-filters">
            <div class="toolbar-grid">
              <v-text-field
                dense outlined hide-details
                :clearable="true"
                label="Broj radnog naloga"
                @keydown.enter="applyFilter()"
                v-model="numberFilter"
                class="filter-select">
              </v-text-field>
              <v-select
                dense outlined hide-details
                v-model="status"
                :items="statuses"
                @change="applyFilter()"
                @click:clear="applyFilter"
                :clearable="true"
                item-text="name"
                item-value="id"
                label="Status"
                class="filter-select">
              </v-select>
              <v-autocomplete
                dense outlined hide-details
                v-model="nameFilter"
                :items="associates"
                @change="applyFilter()"
                @click:clear="applyFilter"
                :clearable="true"
                :item-text="(item) => item.oibs && item.oibs.length > 0 ? `${item.name} ${item.oibs[0]}` : `${item.name}`"
                item-value="name"
                label="Kupac"
                placeholder="Počnite tipkati za pretragu"
                return-object
                class="filter-select uppercased-input">
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.oibs && item.oibs.length > 0">{{ item.oibs[0] }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <date-picker
                style="width: 100% !important"
                :clearable="true"
                prefix-class="xmx"
                :append-to-body="false"
                class="datetimepicker"
                v-model="issueDate"
                type="date"
                valueType="timestamp"
                placeholder="Datum"
                format="DD.MM.YYYY"
                :default-value="new Date()"
                @change="applyFilter()">
              </date-picker>
              <div class="toolbar-grid__actions">
                <v-switch
                  class="mt-0 pt-0"
                  color="#00BCD4"
                  v-model="showAll"
                  label="Prikaži sve"
                  hide-details
                  @change="applyFilter">
                </v-switch>
                <v-btn class="search-btn" @click="applyFilter" elevation="0">
                  <v-icon small class="mr-1">mdi-magnify</v-icon>
                  Pretraži
                </v-btn>
              </div>
            </div>
          </div>
          <div class="wo-rows-body">
          <v-expansion-panels focusable>
            <v-expansion-panel v-for="(workorder, i) in workorders"
              :key="i"
              :class="['wo-panel', getStatusClass(workorder)]"
              tabindex="0">
              <v-expansion-panel-header v-if="workorder"
                hide-actions>
                <v-row>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2">
                    <div class="wo-cell-label text-center">Broj radnog naloga</div>
                    <div class="wo-cell-value text-center" v-html="workorder.workorderNumber"></div>
                  </v-col>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2" style="min-width: 100px; max-width: 100%" class="flex-grow-1 flex-shrink-0">
                    <div class="wo-cell-label">Kupac</div>
                    <div class="wo-customer-name text-capitalize">{{ workorder.customer }}</div>
                  </v-col>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2" class="flex-shrink-0">
                    <div class="wo-cell-label text-right">Iznos</div>
                    <div class="wo-cell-value text-right">{{ workorder.total | money(100, workorder.currency) }}</div>
                  </v-col>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2">
                    <div class="wo-cell-label text-center">Datum</div>
                    <div class="wo-cell-value text-center">{{ workorder.issue_date_time | local }}</div>
                  </v-col>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2">
                    <div class="wo-cell-label text-center">Isporuka</div>
                    <div class="wo-cell-value text-center">{{ workorder.delivery_date | local }}</div>
                  </v-col>
                  <v-col cols="4" sm="4" md="2" lg="2" xl="2">
                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="wo-cell-label text-left">Status</div>
                        <v-chip x-small :class="getStatusChipClass(workorder)">
                          {{ showStatus(workorder.status) }}
                        </v-chip>
                      </div>
                      <div class="d-flex justify-end align-center pl-2" style="z-index: 999">
                        <v-btn min-height="30" min-width="25" plain rounded
                          @click.native.stop
                          @keypress.enter.prevent="openMenu(workorder)"
                          @click="openMenu(workorder)">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" sm="5">
                    <div class="d-flex justify-space-between text-center text-subtitle-2">
                      <span style="font-weight: 600;">Operater:</span> {{ workorder.seller_id }}
                    </div>
                  </v-col>
                </v-row>
                <v-list>
                  <v-row dense>
                    <v-col cols="auto" style="width: 48px; flex: 0 0 48px;">
                      <div class="text-center text-subtitle-2 one-liner-text font-weight-bold">R. br.</div>
                    </v-col>
                    <v-col style="min-width: 0; flex-grow: 1; max-width: none;">
                      <div class="text-left text-subtitle-2 one-liner-text font-weight-bold">Naziv</div>
                    </v-col>
                    <v-col cols="auto" style="width: 80px; flex: 0 0 80px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">Količina</div>
                    </v-col>
                    <v-col cols="auto" style="width: 120px; flex: 0 0 120px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">Jedinična cijena</div>
                    </v-col>
                    <v-col cols="auto" style="width: 100px; flex: 0 0 100px;">
                      <div class="text-right text-subtitle-2 one-liner-text font-weight-bold">Cijena</div>
                    </v-col>
                  </v-row>
                  <v-list-item v-for="(item, index) in workorder.workorder_items"
                    :key="item.id + index"
                    class="pa-0"
                    style="min-height: unset;">
                    <v-row class="pa-0" dense>
                      <v-col cols="auto" style="width: 48px; flex: 0 0 48px;">
                        <div class="text-center text-subtitle-2">{{ index + 1 }}</div>
                      </v-col>
                      <v-col style="min-width: 0; flex-grow: 1; max-width: none;">
                        <div class="text-left text-subtitle-2">{{ item.name }}</div>
                      </v-col>
                      <v-col cols="auto" style="width: 80px; flex: 0 0 80px;">
                        <div class="text-right text-subtitle-2">{{ item.amt / multiplier }}</div>
                      </v-col>
                      <v-col cols="auto" style="width: 120px; flex: 0 0 120px;">
                        <div class="text-right text-subtitle-2">{{ item.single_item_price | money(100, workorder.currency) }}</div>
                      </v-col>
                      <v-col cols="auto" style="width: 100px; flex: 0 0 100px;">
                        <div class="text-right text-subtitle-2">{{ item.price | money(100, workorder.currency) }}</div>
                      </v-col>
                    </v-row>
                  </v-list-item>
                  <template v-if="workorder.tags && workorder.tags.length">
                    <v-divider class="my-2"></v-divider>
                    <v-row v-for="(field, index) in workorder.tags" :key="index" dense>
                      <v-col cols="auto" style="min-width: 160px;">
                        <div class="text-subtitle-2 font-weight-bold">{{ field.field }}</div>
                      </v-col>
                      <v-col>
                        <div class="text-subtitle-2">{{ showFieldValue(field) }}</div>
                      </v-col>
                    </v-row>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          </div>
        </v-col>
      </v-row>
      <infinite-loading ref="InfiniteLoading"
        @infinite="infiniteHandler"
        spinner="waveDots">
        <div slot="no-more"></div>
        <div slot="no-results"></div>
      </infinite-loading>
      <send-email ref="ordinaryEmail"
        @receiptEdit="onReceiptEdit"></send-email>
      <work-order-status-change ref="workOrderStatusChange"
        @receiptEdit="onReceiptEdit"
        @refresh="applyFilter"></work-order-status-change>
    </v-container>
    <create-receipt-from-work-order :associates="associates"
      ref="createReceiptFromWorkOrder"></create-receipt-from-work-order>
    <create-offer-from-work-order :associates="associates"
      ref="createOfferFromWorkOrder"></create-offer-from-work-order>
    <edit-work-order-dialog :associates="associates"
      ref="editWorkOrderDialog"
      @clear-selection="applyFilter"></edit-work-order-dialog>
    <work-order-menu ref="workOrderMenu"
      @menuClicked="onMenuClicked"
      @loader="loader"></work-order-menu>
  </div>
</template>
<script>
import rules from '@/plugins/rules'
import { df, auth, functions } from '@/plugins/firebase'
import state from '@/state'
import { v4 as uuidv4 } from 'uuid'
import InfiniteLoading from 'vue-infinite-loading'
import Confirm from '@/components/Confirm.vue'
import flow from '@/mixins/flow'
import applicationSettings from '@/mixins/applicationSettings'
import { clone, toISOLocal } from '@/plugins/utils'
import SendEmail from '@/modules/company/components/SendEmail'
import WorkOrderStatusChange from '@/modules/point-of-sale/components/workorder/WorkOrderStatusChange'
import { reprint } from '@/libs/bixolon/reprint'
import CreateReceiptFromWorkOrder from '@/modules/point-of-sale/components/workorder/CreateReceiptFromWorkOrder'
import CreateOfferFromWorkOrder from '@/modules/point-of-sale/components/workorder/CreateOfferFromWorkOrder'
import EditWorkOrderDialog from '@/modules/point-of-sale/components/workorder/EditWorkOrderDialog'
import EventBus from '@/plugins/event-bus'
import WorkOrderMenu from '@/modules/point-of-sale/components/workorder/WorkOrderMenu.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/hr.js'
import { callAssociates } from '@/api/associates'

export default {
  components: {
    InfiniteLoading,
    Confirm,
    SendEmail,
    WorkOrderStatusChange,
    CreateReceiptFromWorkOrder,
    WorkOrderMenu,
    EditWorkOrderDialog,
    CreateOfferFromWorkOrder,
    DatePicker
  },
  mixins: [flow, applicationSettings],
  inject: [
    'showMsgBox',
    'showLoader',
    'hideLoader',
    'openPdfPrint',
    'closePdfPrint'
  ],
  data: function () {
    return {
      modal: false,
      loading: false,
      workorders: [],
      receiptsRaw: [],
      unfiskReceipts: [],
      location: {},
      locations: [],
      associates: [],
      statuses: [
        { name: 'Izmjenjen', id: 'UPDATED' },
        { name: 'Kreiran', id: 'CREATED' },
        { name: 'Najava', id: 'INITIAL' },
        { name: 'Otkazan', id: 'CANCELED' },
        { name: 'Povrđen', id: 'CONFIRMED' },
        { name: 'Završen', id: 'COMPLETED' }
      ],
      status: undefined,
      statusFilter: {},
      nameFilter: undefined,
      nameFilterManual: undefined,
      receiptNumberFilter: '',
      receiptNumberParsed: null,
      filterChoosen: '',
      loadingReceipts: false,
      lastVisible: {},
      isExecuted: false,
      listeners: [],
      increment: 1,
      selected: {},
      reciptOffer: {},
      multiplier: 0,
      show: false,
      fiscalizing: false,
      filtersExpanded: true,
      expandIcon: 'mdi-minus',
      rules: {
        req: rules.req(),
        oib: rules.oib()
      },
      loadingDialog: false,
      registerIdFilter: undefined,
      locationIdFilter: undefined,
      lastBalanceTimestampFilter: undefined,
      hideFilter: false,
      numberFilter: undefined,
      issueDate: undefined,
      showAll: false
    }
  },
  computed: {

    now () {
      return parseInt((`${new Date().getTime()}` / 1000).toFixed(0))
    },
    locs: {
      get: function () {
        return this.locations
      },
      set: function (nv) {
        this.locations = [
          { name: this.$t('$vuetify.all'), id: '', location_id: '' }
        ].concat(nv)
      }
    }
  },
  watch: {
    location: {
      deep: true,
      handler (nv, ov) {
        if (nv !== ov) {
          // paginate more columns
          this.lastVisible = {}
          this.receipts = []
          this.unfiskReceipts = []
          this.$refs.InfiniteLoading.stateChanger.reset()
        }
      }
    },
    statusFilter: {
      deep: true,
      handler (nv, ov) {
        if (nv !== ov) {
          // paginate more columns
          this.lastVisible = {}
          this.receipts = []
          this.unfiskReceipts = []
          this.$refs.InfiniteLoading.stateChanger.reset()
        }
      }
    }
  },
  mounted () {
    this.nameFilterManual = ''

    if (this.$attrs.receiptFilter) {
      this.nameFilterManual = this.$attrs.receiptFilter.nameFilter
      this.reciptOffer = this.$attrs.receiptFilter.reciptOffer
      this.modal = this.$attrs.receiptFilter.modal
    }

    if (this.$attrs.receiptFilter?.registerId) {
      this.registerIdFilter = this.$attrs.receiptFilter.registerId
    }
    if (this.$attrs.receiptFilter?.locationId) {
      this.locationIdFilter = this.$attrs.receiptFilter.locationId
    }
    if (this.$attrs.receiptFilter?.lastBalanceTimestamp) {
      this.lastBalanceTimestampFilter = this.$attrs.receiptFilter.lastBalanceTimestamp
    }
    if (this.$attrs.receiptFilter?.hideFilter) {
      this.hideFilter = this.$attrs.receiptFilter.hideFilter
    }

    // this.getAssociates()
    this.getMultiplier()
    this.locations = [
      { name: this.$t('$vuetify.all'), id: '', location_id: '' }
    ]
    // this.location = this.locations[0]

    // this.reciptOffer = this.reciptOfferChoices[0].value

    const docRef = df.doc(
      `user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`
    )
    const listener = docRef.onSnapshot(doc => {
      if (
        doc &&
        doc.data() &&
        doc.data().locations &&
        Object.keys(doc.data().locations).length > 0
      ) {
        this.locs = Object.keys(doc.data().locations).map(key => {
          return doc.data().locations[key]
        })
      }
    })
    this.listeners.push(listener)

    this.receiptNumberFilter = ''

    EventBus.$off('stopButtonLoader')
    EventBus.$on('stopButtonLoader', (data) => {
      this.loadingDialog = false
    })
  },
  methods: {
    getStatusClass (workorder) {
      const s = workorder.status?.current_status
      if (s === 'COMPLETED') return 'wo-status-ok'
      if (s === 'CANCELED') return 'wo-status-failed'
      return 'wo-status-processing'
    },
    getStatusChipClass (workorder) {
      const s = workorder.status?.current_status
      if (s === 'COMPLETED') return 'wo-chip-ok'
      if (s === 'CANCELED') return 'wo-chip-failed'
      return 'wo-chip-processing'
    },
    loader () {
      this.loading = true
    },
    showFieldValue (field) {
      if (typeof field.value === 'number') {
        const date = new Date(field.value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('hr-HR')
        }
      } else if (typeof field.value === 'boolean') {
        return field.value ? 'Da' : 'Ne'
      } else if (typeof field.value === 'string') {
        return field.value
      }

      return ''
    },
    showStatus (status) {
      if (status?.current_status) {
        switch (status.current_status) {
          case 'UPDATED':
            return 'IZMJENJEN'
          case 'CREATED':
            return 'KREIRAN'
          case 'INITIAL':
            return 'NAJAVA'
          case 'CANCELED':
            return 'OTKAZAN'
          case 'CONFIRMED':
            return 'POTVRĐEN'
          case 'COMPLETED':
            return 'ZAVRŠEN'
        }
      } else {
        return 'KREIRAN'
      }
    },
    editWorkOrder (workorder) {
      this.loadingDialog = true
      this.$refs.editWorkOrderDialog.open(this.loadingDialog, workorder)
    },
    createOfferFromWorkOrder (workorder) {
      this.loadingDialog = true
      this.$refs.createOfferFromWorkOrder.open(this.loadingDialog, workorder)
    },
    createReceiptFromWorkOrder (workorder) {
      this.loadingDialog = true
      this.$refs.createReceiptFromWorkOrder.open(this.loadingDialog, workorder)
    },
    changeWorkOrderStatus (workorder) {
      this.loadingDialog = true
      this.$refs.workOrderStatusChange.open(workorder)
    },
    changeBalanceTimestampFilter (balanceTimestamp) {
      if (balanceTimestamp) {
        this.lastBalanceTimestampFilter = balanceTimestamp
      }
      this.applyFilter()
    },
    setReceiptType (receiptOffer) {
      this.reciptOffer = receiptOffer
    },
    printWorkOrder (workorder) {
      this.showLoader()
      var printTemplate = ''
      if (state.getCurrentCompany().print_template && state.getCurrentCompany().print_template !== '') {
        printTemplate = state.getCurrentCompany().print_template
      }

      var receipttemplate = functions.httpsCallable('receiptTemplate')

      receipttemplate({
        template: printTemplate,
        companyId: workorder.company_details.id,
        documentId: workorder.id,
        documentType: 'radninalog',
        rawJson: ''
      })
        .then((result) => {
          this.hideLoader()
          if (result.data?.document !== '') {
            this.openPdfPrint(result.data?.document)
          }
        }
        )
    },
    openCrudDialog () {
      this.loadingDialog = true
      this.$refs.crudTemplateDialog.open(this.loadingDialog)
    },
    async onMenuClicked (item, workorder) {
      try {
        this.loading = true // Pokreni loader prije akcije
        await item.action(workorder) // Pričekaj da se akcija završi
      } catch (error) {
        console.error('Greška u akciji:', error)
      } finally {
        this.loading = false // Ukloni loader nakon što se akcija završi
      }
    },
    openMenu (workorder) {
      this.$refs.workOrderMenu.open(workorder, 0)
    },
    onReceiptReprint (receipt) {
      reprint('Printer1', receipt, 2)
    },

    reprintReceipt (receipt) {
      reprint('Printer1', receipt, 0)
    },

    applyFilter () {
      this.lastVisible = {}
      this.workorders = []
      this.$refs.InfiniteLoading.stateChanger.reset()
    },

    issueBefore (issueDateTime) {
      const tmp = new Date(
        toISOLocal(
          new Date(issueDateTime * 1000)
        )
      )

      tmp.setDate(tmp.getDate() + 2)

      const before = parseInt(
        (tmp.getTime() / 1000).toFixed(0)
      )

      return before
    },
    navigateToFlow () {
      this.$router.push({
        name: 'Home',
        params: {
        }
      })
    },
    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        this.config = doc.data()
        this.multiplier = doc.data().item_amount.decimal_multiplier
      })
      this.listeners.push(listener)
    },
    modifyItems (change) {
      if (change.type === 'added') {
        if (change.doc.data()) {
          const _document = clone(change.doc.data())

          _document.workorderOptions = [
            { title: 'Uredi radni nalog', action: this.editWorkOrder },
            { title: 'Preuzmi radni nalog', action: this.printWorkOrder },
            { title: 'Pošalji radni nalog E-mailom', action: this.sendWorkOrderEmail },
            { title: 'Kreiraj ponudu', action: this.createOfferFromWorkOrder },
            { title: 'Kreiraj račun', action: this.createReceiptFromWorkOrder },
            { title: 'Promijeni status', action: this.changeWorkOrderStatus }
          ]

          _document.customer = _document.type.payer_name

          if (_document.designation) {
            _document.workorderNumber = `RN-${_document.designation.number}`
          }

          if (_document.issue_date_time) {
            _document.time = _document.issue_date_time.seconds
          }

          _document.show = false

          this.workorders.push({
            ..._document,
            ...{ [`hover-${change.doc.data().id}`]: false }
          })
          // }

          this.workorders.sort((a, b) => b.issue_date_time - a.issue_date_time) // b - a for reverse sort
          this.$forceUpdate()
        }
      }
      if (change.type === 'modified') {
        if (this.receipts) {
          this.receipts = this.receipts.map(item => {
            let ret = item
            if (item.id === change.doc.data().id) {
              const _document = clone(change.doc.data())

              _document.workorderOptions = [
                { title: 'Uredi radni nalog', action: this.editWorkOrder },
                { title: 'Preuzmi radni nalog', action: this.printWorkOrder },
                { title: 'Pošalji radni nalog E-mailom', action: this.sendWorkOrderEmail },
                { title: 'Kreiraj ponudu', action: this.createOfferFromWorkOrder },
                { title: 'Kreiraj račun', action: this.createReceiptFromWorkOrder },
                { title: 'Promijeni status', action: this.changeWorkOrderStatus }
              ]

              _document.customer = _document.type.payer_name

              if (_document.designation) {
                _document.workorderNumber = `${_document.designation.number}`
              }

              if (_document.issue_date_time) {
                _document.time = _document.issue_date_time.seconds
              }

              ret = _document
            }
            return { ...ret, ...{ [`hover-${change.doc.data().id}`]: false } }
          })
        }
      }

      this.workorders = this.workorders.filter((document, index, self) =>
        index === self.findIndex((t) => (
          t.id === document.id
        ))
      )
    },
    infiniteHandler ($state) {
      const that = this
      const where = that.location ? that.location.location_id : ''

      let query = df
        .collection(
          `work_orders/${state.getCurrentCompany().id}/work_orders`
        )
        .orderBy('issue_date_time', 'desc')
        .startAfter(that.lastVisible)
        .limit(10)

      if (where) {
        query = query.where('designation.location_id', '==', where)
      }

      if (!this.showAll && this.status !== 'CANCELED' && this.status !== 'COMPLETED') {
        query = query.where('status.current_status', 'in', ['INITIAL', 'CREATED', 'UPDATED', 'CONFIRMED'])
      }

      if (this.numberFilter) {
        query = query.where('designation.number', '==', parseInt(this.numberFilter))
      }

      if (this.status) {
        query = query.where('status.current_status', '==', this.status)
      }

      if (this.issueDate) {
        const endDt = new Date(this.issueDate)

        // Postavi vrijeme na 23:59:59
        endDt.setHours(23)
        endDt.setMinutes(59)
        endDt.setSeconds(59)

        // Vrati timestamp za 23:59:59
        var end = endDt.getTime()

        query = query.where('issue_date_time', '>=', this.issueDate / 1000)
        query = query.where('issue_date_time', '<=', end / 1000)
      }

      const nameFilter = this.nameFilter

      if (nameFilter) {
        // provjera da li je uneseni filter broj (oib) ili ne
        let argument
        if (!isNaN(nameFilter)) {
          // nameFilter je OIB
          argument = nameFilter
        } else {
          if (typeof (nameFilter) === 'object') {
            // nameFilter je račun
            argument = nameFilter.name
          } else if (typeof (nameFilter) === 'string') {
            // nameFilter je string
            argument = nameFilter
          }
        }
        query = query.where('type.payer_name', '==', argument)
      }

      try {
        const listener = query.onSnapshot(
          doc => {
            doc.docChanges().forEach(change => {
              that.modifyItems(change)
            })
            if (doc && !doc.empty) {
              if (doc.docs[doc.docs.length - 1]) {
                that.lastVisible = doc.docs[doc.docs.length - 1]
              }
              $state.loaded()
            } else {
              $state.complete()
            }
          }

        )

        this.listeners.push(listener)
      } catch (error) {
        console.log(error)
      }
      this.workorders = this.workorders.sort((a, b) => { return a.issue_date_time < b.issue_date_time }

      )
    },
    getReceiptsOrOffers () {
      this.lastVisible = ''
      this.receipts = []
      this.unfiskReceipts = []
      this.detachListeners()

      if (this.$refs.InfiniteLoading) {
        this.$refs.InfiniteLoading.stateChanger.reset()
      }
    },
    stornoReceipt (receipt) {
      this.$refs.confirmStorno.open(
        '',
        `Storniraj račun ${receipt.receiptNumber}?`,
        { confirmText: 'Da', cancelText: 'Ne' }
      )
      this.selected = receipt
    },
    sendWorkOrderEmail (receipt) {
      this.$refs.ordinaryEmail.open(receipt, {
        action: 'WORKORDER_SENT',
        title: 'Pošalji radni nalog mailom',
        okBtn: 'Pošalji Email'
      })
    },

    printReceipt (receipt, preview = false) {
      const that = this
      try {
        that.showLoader()
        const reqId = uuidv4()
        let entity = 'receipt'
        if (this.reciptOffer === 'offers') {
          entity = 'offer'
        }

        if (this.reciptOffer === 'virmans') {
          entity = 'virman'
        }

        const payload = {
          action: {
            operation: 'get',
            entity: entity,
            resource_id: receipt.id,
            params: {
              company_id: state.getCurrentCompany().id,
              print_size: 'A4'
            }
          }
        }

        if (that.reciptOffer === 'offers') {
          payload.action.entity = 'offer'
          payload.action.params.print_size = 'A4'
        }

        if (that.reciptOffer === 'virmans') {
          payload.action.entity = 'virman'
          payload.action.params.print_size = 'A4'
        }

        df.doc(`request/${reqId}`)
          .set({
            user_id: `${auth.currentUser.uid}`,
            device_id: 'web',
            created: `${new Date().getTime()}`,
            type: 'document',
            payload: btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
          })
          .then(function () { })
          .catch(function (err) {
            that.hideLoader()
            that.showMsgBox({
              text: err && err !== '' ? err : 'An error has occurred',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
          })

        const docRef = df.doc(`response/${reqId}`)
        const unsubscribe = docRef.onSnapshot(function (doc) {
          if (that.$refs && doc.data()) {
            // this.$refs.createBusinessForm.reset()
            unsubscribe()
            if (!doc.data().body || !doc.data().body.document) {
              that.showMsgBox({
                text: that.$t('$vuetify.errors.nullReceipt'),
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              return
            }

            if (preview) {
              that.openPdfPrint(doc.data().body.document)
              that.hideLoader()
            } else {
              const linkSource = `data:application/pdf;base64,${doc.data().body.document
                }`
              const downloadLink = document.createElement('a')
              const fileName = `${receipt.receiptNumber}.pdf`
              downloadLink.href = linkSource
              downloadLink.download = fileName
              downloadLink.click()
              that.hideLoader()
            }
          }
        })
      } catch (err) {
        that.hideLoader()
        this.showMsgBox({
          text: err && err !== '' ? err : 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    cancelReceipt () {
      const that = this
      try {
        const receipt = Object.assign({}, this.selected)
        this.selected = {}
        const reqId = uuidv4()

        let receiptNumber = receipt.designation ? receipt.designation.number : 0
        df.doc(`cash_registers/${receipt.designation.register_id}`)
          .get()
          .then(reg => {
            if (reg && reg.exists) {
              if (reg.data()) {
                if (reg.data().status !== 'OK') {
                  this.$refs.confirmStorno.close()
                  that.showMsgBox({
                    text: that.$t('$vuetify.errors.unknownRegister'),
                    actions: ['cancel'],
                    cancelBtnText: that.$t('$vuetify.close'),
                    color: 'error'
                  })
                  return
                }
                if (this.reciptOffer === 'receipts') {
                  receiptNumber = reg.data().bill_sequence || 0
                } else if (this.reciptOffer === 'offers') {
                  receiptNumber = reg.data().offer_sequence || 0
                }
                const payload = {
                  action: {
                    operation: 'set',
                    entity: 'storno',
                    params: {
                      company_id: state.getCurrentCompany().id,
                      location_id: receipt.location_details.id,
                      receipt_id: receipt.id,
                      register_id: receipt.designation.register_id,
                      issue_date_time: parseInt(
                        (new Date().getTime() / 1000).toFixed(0)
                      ),
                      number: receiptNumber + 1
                      // seller_id: auth.currentUser.uid,
                    }
                  }
                }

                that.showLoader()
                that.$refs.confirmStorno.close()
                df.doc(`request/${reqId}`)
                  .set({
                    user_id: `${auth.currentUser.uid}`,
                    device_id: 'web',
                    created: `${new Date().getTime()}`,
                    type: 'receipt',
                    payload: btoa(
                      unescape(encodeURIComponent(JSON.stringify(payload)))
                    )
                  })
                  .then(function () {
                    setTimeout(() => {
                      that.hideLoader()
                      that.lastVisible = {}
                      that.receipts = []
                      that.unfiskReceipts = []
                      that.$refs.InfiniteLoading.stateChanger.reset()
                    }, 5500)
                  })
                  .catch(function (err) {
                    that.hideLoader()
                    that.showMsgBox({
                      text: err && err !== '' ? err : 'An error has occurred',
                      actions: ['cancel'],
                      cancelBtnText: 'OK',
                      color: 'error'
                    })
                  })
              }
            } else {
              that.showMsgBox({
                text: that.$t('$vuetify.errors.unknownRegister'),
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              this.$refs.confirmStorno.close()
            }
          })
      } catch (err) {
        this.$refs.confirmStorno.close()
        that.hideLoader()
        that.showMsgBox({
          text: err || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    onReceiptEdit (receipt) {
      this.receipts = this.receipts.map(r => {
        if (r.id === receipt.id) {
          r.payment.current_status = 'PROCESSING'
        }
        return r
      })
    },
    fiscalizeReceipt () {
      const that = this
      try {
        const receipt = Object.assign({}, this.selected)
        this.selected = {}
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'update',
            entity: 'fiscalize',
            resource_id: receipt.id,
            params: {
              company_id: state.getCurrentCompany().id
            }
          }
        }

        that.showLoader()
        that.$refs.confirmFiscalize.close()
        df.doc(`request/${reqId}`)
          .set({
            user_id: `${auth.currentUser.uid}`,
            device_id: 'web',
            created: `${new Date().getTime()}`,
            type: 'receipt',
            payload: btoa(
              unescape(encodeURIComponent(JSON.stringify(payload)))
            )
          })
          .then(function () {
          })
          .catch(function (err) {
            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
            that.showMsgBox({
              text: err && err !== '' ? err : 'An error has occurred',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
          })

        const docRef = df.doc(`response/${reqId}`)
        const unsubscribe = docRef.onSnapshot(function (doc) {
          if (that.$refs && doc.data()) {
            unsubscribe()
            if (!doc.data().header || !doc.data().header.code || doc.data().header.code !== 200) {
              that.showMsgBox({
                text: doc.data().header.error ? doc.data().header.error : 'Nije uspjela fiskalizacija računa!',
                actions: ['cancel'],
                cancelBtnText: that.$t('$vuetify.close'),
                color: 'error'
              })
              that.hideLoader()
              that.lastVisible = {}
              that.receipts = []
              that.unfiskReceipts = []
              that.$refs.InfiniteLoading.stateChanger.reset()
              that.$refs.confirmFiscalize.close()
              return
            }

            that.hideLoader()
            that.lastVisible = {}
            that.receipts = []
            that.unfiskReceipts = []
            that.$refs.InfiniteLoading.stateChanger.reset()
            that.$refs.confirmFiscalize.close()
          }
        })
      } catch (err) {
        that.hideLoader()

        that.lastVisible = {}
        that.receipts = []
        that.unfiskReceipts = []
        that.$refs.InfiniteLoading.stateChanger.reset()
        that.$refs.confirmFiscalize.close()
        that.showMsgBox({
          text: err || 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },

    onExpandFilters () {
      this.filtersExpanded = !this.filtersExpanded
      this.expandIcon = this.filtersExpanded ? 'mdi-minus' : 'mdi-plus'
    },

    parseReceiptNumber (receiptNumber) {
      receiptNumber = receiptNumber.replaceAll('/', '-')
      const parts = receiptNumber.split('-')
      if (parts.length !== 3) {
        return {
          number: 0,
          location_id: '',
          register_number: 0
        }
      }
      return { number: parseInt(parts[0]), location_id: parts[1], register_number: parseInt(parts[2]) }
    },

    async getAssociates () {
      var path = `/associates/${state.getCurrentCompany().id}/associate`
      var response = await callAssociates('get', path)

      if (response && response.status === 200 && response.data && response.data.associates) {
        this.defaultAssociates = Object.keys(response.data.associates)
          .map((key) => {
            return response.data.associates[key]
          })
          .filter((associate) => associate.status === 'OK' && associate.associate_type.BUYER)

        this.defaultAssociates.push({ name: 'Kupac građanin', id: 'kupac-gradanin', oib: '' })

        this.associates = clone(this.defaultAssociates)
      } else if (response.status === 404) {
        this.associates = []
        this.showMsgBox({
          text: 'Klijenti nisu pronađeni.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'warning'
        })
      } else {
        this.showMsgBox({
          text: 'Neuspjelo dohvaćanje klijenata',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },

    resetField (field) {
      field === 'name' ? this.nameFilterManual = '' : this.nameFilter = undefined
      if (field === 'status') this.status = undefined
      this.applyFilter()
    }
  }
}
</script>
<style scoped>
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 8;
  background: #ffffff;
  padding: 14px 16px 16px;
  margin-bottom: 0;
  border-bottom: 1px solid #EEF2F5;
  border-left: 3px solid #00BCD4;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.toolbar-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  align-items: center;
}

.toolbar-grid > * {
  align-self: center;
  margin-top: 0 !important;
}

.toolbar-grid__actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  align-self: center !important;
  margin-left: 8px;
}

@media (max-width: 900px) {
  .toolbar-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.filter-select {
  min-width: 0 !important;
}

.filter-select.v-text-field--outlined >>> fieldset {
  border-color: #B0BEC5 !important;
  border-width: 1.5px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: #90A4AE !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #00838F !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: #ffffff !important;
  min-height: 38px !important;
  max-height: 38px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-input__control {
  height: 38px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-text-field__slot {
  height: 38px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #263238 !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #37474F !important;
}

.filter-select >>> .v-input__prepend-inner {
  display: flex !important;
  align-items: center !important;
  align-self: center !important;
  margin-top: 0 !important;
}

.filter-select >>> .v-input__prepend-inner .v-icon,
.filter-select >>> .v-input__append-inner .v-icon {
  color: #455A64 !important;
}

.search-btn {
  align-self: center !important;
  margin-top: 0 !important;
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
  border: 1.2px solid #00b3be !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  transition: background 0.15s ease !important;
  padding: 0 16px !important;
  height: 38px !important;
  min-height: 38px !important;
  max-height: 38px !important;
  min-width: 100px !important;
  font-weight: 600 !important;
  margin: 0 !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}

.search-btn:hover {
  background: rgba(0, 188, 212, 0.15) !important;
}

.search-btn .v-icon {
  color: #00838F !important;
}

.wo-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}

.wo-rows-body .v-expansion-panels {
  background: transparent !important;
}

.wo-panel {
  border: 1px solid #EEF2F5 !important;
  border-left: 3px solid #E0E0E0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  overflow: hidden !important;
  margin-bottom: 4px !important;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.wo-panel:hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}

.wo-panel::before,
.wo-panel::after {
  box-shadow: none !important;
  display: none !important;
}

.wo-panel.wo-status-ok    { border-left-color: #4ADE80 !important; }
.wo-panel.wo-status-failed { border-left-color: #F87171 !important; }
.wo-panel.wo-status-processing { border-left-color: #FBBF24 !important; }

.wo-panel >>> .v-expansion-panel-header {
  min-height: 60px !important;
  padding: 8px 16px !important;
}

.wo-cell-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 2px;
}

.wo-cell-value {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
}

.wo-customer-name {
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}

.wo-chip-ok          { background-color: #DCFCE7 !important; color: #166534 !important; }
.wo-chip-failed      { background-color: #FEE2E2 !important; color: #991B1B !important; }
.wo-chip-processing  { background-color: #FEF9C3 !important; color: #854D0E !important; }
</style>

<style>
::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

</style>
